import React, { useState } from 'react';
import Spinner from '@atlaskit/spinner';
import { Form, Formik, FormikHelpers } from 'formik';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useQuery } from 'urql';
import { object, string } from 'yup';

import { Button, SimpleHeader } from '@/components';
import Field from '@/components/forms/Field';
import { ActivateViaEmail, PasswordForm } from '@/components/forms/forms';
import userQuery from '@/graphql/queries/user.graphql';
import { useIsMobile, useStore } from '@/hooks';
import { trackEvent } from '@/lib/GA';
import { getServerSidePropsWithGlobalData } from '@/lib/getServerSideProps';
import { withUrql } from '@/lib/urqlClient';
import UserService from '@/lib/UserService';
import { getQueryParam } from '@/lib/Utils';

import styles from './styles.module.scss';

interface SendActivationForm {
  email: string;
}

const ActivationPage = ({}) => {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [showCheck, setShowCheck] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const [isMobile] = useIsMobile(768);
  const [error, setError] = useState('');
  const [verInvalid, setVerInvalid] = useState(false);
  const { email } = useStore('globals');
  const code = getQueryParam(router.query.code);
  const id = getQueryParam(router.query.id);

  const [{ data: userData }] = useQuery<UserQuery, UserQueryVariables>({
    query: userQuery,
    variables: {
      uuid: id,
      code,
    },
  });

  const sendActivationEmail = async (values: SendActivationForm) => {
    setLoading(true);
    await UserService.sendActivationEmail(values.email);
    setShowCheck(true);
    setLoading(false);
    trackEvent('Membership Activation', 'Clicked Activate Membership');
  };

  return (
    <div className={styles.activate} data-test-id="activate">
      <div className={styles.inner}>
        {isMobile ? (
          <SimpleHeader href="/">
            <h3>{showCheck && !inProgress && !loading ? 'Activation Request Received' : 'Activate your membership'}</h3>
          </SimpleHeader>
        ) : (
          <h1 className={styles.title}>
            {showCheck && !inProgress && !loading ? 'Activation Request Received' : 'Activate your membership'}
          </h1>
        )}

        {code && id && !verInvalid && (
          <p>Your new password must have a combination of letters, numbers and special characters.</p>
        )}
        {error && <div className={styles.error}>{error}</div>}

        {verInvalid &&
          (!showCheck ? (
            <div className={styles.invalidVerification}>
              <EmailForm
                loading={loading}
                sendActivationEmail={sendActivationEmail}
                initialValues={{ email: userData?.userDetails?.email || '' }}
              >
                <p>Your account activation token is no longer valid</p>
                <div className={styles.subtext}>
                  This can happen if you clicked on an old or outdated activation link.
                </div>
              </EmailForm>
            </div>
          ) : (
            <p>
              Your have been sent another activation email.
              <br /> Make sure you click the most recent email link otherwise it will not work.
            </p>
          ))}

        {code && id && !verInvalid && (
          <PasswordForm
            id={id}
            code={code}
            setError={setError}
            setVerInvalid={setVerInvalid}
            submitText="Activate your membership"
            onSuccess={() => window.location.assign('/update')}
            userEmail={userData?.userDetails?.email || undefined}
          />
        )}

        {/* Logic for /activate with no code or id in query params */}
        {!code && !id && (
          <div className={styles.sendActivation}>
            {!showCheck ? (
              <ActivateViaEmail
                loading={loading}
                setLoading={setLoading}
                setInProgress={setInProgress}
                setShowCheck={setShowCheck}
                setError={setError}
              />
            ) : (
              <div className={styles.manualSubmissionMsg}>
                {inProgress ? (
                  <>
                    <div>
                      We cannot find a matching account. We may still be completing the account registration process.
                    </div>
                    <div>
                      We’ve been notified that you were not able to activate your account. Please contact our support
                      team at <a href={`mailto:${email}`}>{email}</a> if you require assistance.
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      Your request has been processed. If there&apos;s an account associated with the provided email
                      address, an activation email will be dispatched to it.
                    </div>
                    <br />
                    <div>
                      If you haven&apos;t received an email, please <a href="/activate">try again</a> or contact us at{' '}
                      <a href={`mailto:${email}`}>{email}</a>.
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

interface EmailFormProps {
  loading: boolean;
  children?: React.ReactNode;
  initialValues: SendActivationForm;
  sendActivationEmail: (
    values: SendActivationForm,
    formikHelpers: FormikHelpers<SendActivationForm>,
  ) => void | Promise<any>;
}

const EmailForm = ({ loading, children, initialValues, sendActivationEmail }: EmailFormProps) => (
  <Formik<SendActivationForm>
    initialValues={initialValues}
    onSubmit={sendActivationEmail}
    validationSchema={EmailForm.validationSchema}
  >
    {({ isValid }) => (
      <Form>
        {children}
        <Field label="Email" type="email" name="email" formik required />
        {loading ? (
          <div className={styles.spinner}>
            <Spinner size="large" />
          </div>
        ) : (
          <Button type="submit" disabled={!isValid}>
            Send activation email
          </Button>
        )}
      </Form>
    )}
  </Formik>
);

EmailForm.validationSchema = object({ email: string().email().required() });

export const getServerSideProps: GetServerSideProps = async (ctx) =>
  await getServerSidePropsWithGlobalData(ctx, async ({ nextCtx, stores, urqlClient }) => {
    // Reroute user to update page if they are already logged in and hit this page
    if (stores.userStore.loggedIn) {
      return {
        redirect: {
          destination: '/update',
          permanent: false,
        },
      };
    }

    const code = getQueryParam(nextCtx.query?.code);
    const id = getQueryParam(nextCtx.query?.id);

    await urqlClient
      .query<UserQuery, UserQueryVariables>(userQuery, {
        uuid: id,
        code,
      })
      .toPromise();

    return { props: undefined };
  });

export default withUrql(ActivationPage);
